import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { Plus, Trash2, Download, X, RotateCw, Edit2, BookMarked } from "lucide-react";

/**
 * JournalTab — Admin-only manual double-entry journal book.
 *
 * Endpoints:
 *   GET     /back-office/books/{book_id}/journal-entries[?start&end&account]
 *   POST    /back-office/books/{book_id}/journal-entries
 *   PATCH   /back-office/books/{book_id}/journal-entries/{je_id}
 *   DELETE  /back-office/books/{book_id}/journal-entries/{je_id}
 *   POST    /back-office/books/{book_id}/journal-entries/{je_id}/reverse
 *   GET     /back-office/books/{book_id}/journal-entries.csv?start&end&lang
 */

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const Modal = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white max-w-3xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`${testid}-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* ------------- Entry-form (new + edit) -------------- */
const EntryForm = ({ book, accounts, initial, onCancel, onSaved, lang }) => {
  const isEdit = Boolean(initial);
  const [date, setDate] = useState(initial?.date || todayISO());
  const [memo, setMemo] = useState(initial?.memo || "");
  const emptyLine = { account_code: "", debit: "", credit: "", description: "" };
  const [lines, setLines] = useState(() =>
    initial?.lines?.length
      ? initial.lines.map((l) => ({ ...l, debit: l.debit || "", credit: l.credit || "" }))
      : [emptyLine, emptyLine]
  );
  const [busy, setBusy] = useState(false);

  const setLine = (i, k, v) => setLines((prev) => prev.map((ln, idx) => (idx === i ? { ...ln, [k]: v } : ln)));
  const addLine = () => setLines((p) => [...p, emptyLine]);
  const removeLine = (i) => setLines((p) => (p.length > 2 ? p.filter((_, idx) => idx !== i) : p));

  const totals = useMemo(() => {
    const dr = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const cr = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { dr, cr, diff: Math.round((dr - cr) * 100) / 100, balanced: Math.abs(dr - cr) < 0.005 && dr > 0 };
  }, [lines]);

  const nonHeaderAccounts = useMemo(() => accounts.filter((a) => !a.is_header), [accounts]);

  const submit = async (e) => {
    e.preventDefault();
    if (!totals.balanced) {
      toast.error(lang === "es" ? "Los débitos deben igualar los créditos" : "Debits must equal credits");
      return;
    }
    const clean = lines
      .filter((l) => l.account_code && (Number(l.debit) > 0 || Number(l.credit) > 0))
      .map((l) => ({
        account_code: l.account_code,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        description: l.description || "",
      }));
    if (clean.length < 2) {
      toast.error(lang === "es" ? "Se requieren al menos 2 líneas" : "At least 2 lines required");
      return;
    }
    setBusy(true);
    try {
      const url = `/back-office/books/${book.id}/journal-entries${isEdit ? `/${initial.id}` : ""}`;
      const method = isEdit ? api.patch : api.post;
      const { data } = await method(url, { date, memo, lines: clean });
      toast.success(isEdit ? (lang === "es" ? "Actualizado" : "Updated") : (lang === "es" ? "Creado" : "Created"));
      onSaved(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (lang === "es" ? "Error al guardar" : "Failed to save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="je-form">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Fecha" : "Date"}</label>
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="rn3-input w-full" data-testid="je-date" />
        </div>
        <div className="col-span-2">
          <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Memo" : "Memo"}</label>
          <input required value={memo} onChange={(e) => setMemo(e.target.value)} className="rn3-input w-full" placeholder={lang === "es" ? "Descripción del asiento" : "Entry description"} data-testid="je-memo" />
        </div>
      </div>

      <div>
        <div className="grid grid-cols-12 gap-2 pb-2 border-b border-[#E5E5E5]">
          <div className="col-span-4 rn3-eyebrow">{lang === "es" ? "Cuenta" : "Account"}</div>
          <div className="col-span-3 rn3-eyebrow">{lang === "es" ? "Descripción" : "Description"}</div>
          <div className="col-span-2 rn3-eyebrow text-right">{lang === "es" ? "Débito" : "Debit"}</div>
          <div className="col-span-2 rn3-eyebrow text-right">{lang === "es" ? "Crédito" : "Credit"}</div>
          <div className="col-span-1"></div>
        </div>

        {lines.map((line, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 py-2 border-b border-[#F5F5F5] items-center" data-testid={`je-line-${i}`}>
            <select
              value={line.account_code}
              onChange={(e) => setLine(i, "account_code", e.target.value)}
              className="rn3-input col-span-4 text-sm"
              data-testid={`je-line-${i}-account`}
            >
              <option value="">{lang === "es" ? "-- Seleccionar cuenta --" : "-- Select account --"}</option>
              {nonHeaderAccounts.map((a) => (
                <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
              ))}
            </select>
            <input
              value={line.description}
              onChange={(e) => setLine(i, "description", e.target.value)}
              className="rn3-input col-span-3 text-sm"
              placeholder={lang === "es" ? "opcional" : "optional"}
              data-testid={`je-line-${i}-desc`}
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={line.debit}
              onChange={(e) => setLine(i, "debit", e.target.value)}
              className="rn3-input col-span-2 text-sm text-right"
              placeholder="0.00"
              data-testid={`je-line-${i}-debit`}
              disabled={Number(line.credit) > 0}
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={line.credit}
              onChange={(e) => setLine(i, "credit", e.target.value)}
              className="rn3-input col-span-2 text-sm text-right"
              placeholder="0.00"
              data-testid={`je-line-${i}-credit`}
              disabled={Number(line.debit) > 0}
            />
            <button
              type="button"
              onClick={() => removeLine(i)}
              className="col-span-1 text-[#737373] hover:text-[#8B1C22] disabled:opacity-30"
              disabled={lines.length <= 2}
              data-testid={`je-line-${i}-remove`}
            >
              <Trash2 className="w-4 h-4 mx-auto" />
            </button>
          </div>
        ))}

        <div className="mt-3 flex items-center justify-between">
          <button type="button" onClick={addLine} className="text-xs text-[#8B1C22] flex items-center gap-1" data-testid="je-add-line">
            <Plus className="w-3 h-3" /> {lang === "es" ? "Agregar línea" : "Add line"}
          </button>
          <div className={`text-sm ${totals.balanced ? "text-[#15803D]" : "text-[#8B1C22]"}`} data-testid="je-totals">
            <span>DR {fmt(totals.dr)}</span>
            <span className="mx-3 text-[#C4A252]">·</span>
            <span>CR {fmt(totals.cr)}</span>
            {!totals.balanced && (
              <span className="ml-3 font-medium">
                ({lang === "es" ? "Diferencia" : "Diff"}: {fmt(Math.abs(totals.diff))})
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[#E5E5E5]">
        <button type="button" onClick={onCancel} className="rn3-btn-ghost flex-1 justify-center" data-testid="je-cancel">
          {lang === "es" ? "Cancelar" : "Cancel"}
        </button>
        <button type="submit" disabled={busy || !totals.balanced} className="rn3-btn-primary flex-1 justify-center" data-testid="je-submit">
          {busy ? "…" : (isEdit ? (lang === "es" ? "Guardar cambios" : "Save changes") : (lang === "es" ? "Crear asiento" : "Create entry"))}
        </button>
      </div>
    </form>
  );
};

/* ------------------ Main tab ---------------------- */
const JournalTab = ({ book, accounts, lang }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ start: "", end: "", account: "" });
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start) params.set("start", filters.start);
      if (filters.end) params.set("end", filters.end);
      if (filters.account) params.set("account", filters.account);
      const { data } = await api.get(`/back-office/books/${book.id}/journal-entries?${params.toString()}`);
      setItems(data);
    } catch {
      toast.error(lang === "es" ? "Error al cargar" : "Failed to load journal entries");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [book.id]);

  const reverse = async (je) => {
    if (!confirm(lang === "es" ? `¿Revertir "${je.memo}"?` : `Reverse "${je.memo}"?`)) return;
    try {
      await api.post(`/back-office/books/${book.id}/journal-entries/${je.id}/reverse`);
      toast.success(lang === "es" ? "Revertido" : "Reversed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "es" ? "Error al revertir" : "Failed to reverse"));
    }
  };

  const remove = async (je) => {
    if (!confirm(lang === "es" ? `¿Eliminar "${je.memo}"?` : `Delete "${je.memo}"?`)) return;
    try {
      await api.delete(`/back-office/books/${book.id}/journal-entries/${je.id}`);
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "es" ? "Error al eliminar" : "Failed to delete"));
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ lang });
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);
    const token = localStorage.getItem("rn3_token");
    const url = `${api.defaults.baseURL}/back-office/books/${book.id}/journal-entries.csv?${params.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `journal_entries_${book.name}.csv`;
        a.click();
      });
  };

  const nonHeaderAccounts = useMemo(() => accounts.filter((a) => !a.is_header), [accounts]);

  return (
    <div className="space-y-5" data-testid="journal-tab">
      {/* Filters + actions */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Desde" : "From"}</label>
          <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="rn3-input text-sm" data-testid="je-filter-start" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Hasta" : "To"}</label>
          <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="rn3-input text-sm" data-testid="je-filter-end" />
        </div>
        <div>
          <label className="rn3-eyebrow block mb-1">{lang === "es" ? "Cuenta" : "Account"}</label>
          <select value={filters.account} onChange={(e) => setFilters({ ...filters, account: e.target.value })} className="rn3-input text-sm" data-testid="je-filter-account">
            <option value="">{lang === "es" ? "-- Todas --" : "-- All --"}</option>
            {nonHeaderAccounts.map((a) => (<option key={a.code} value={a.code}>{a.code} — {a.name}</option>))}
          </select>
        </div>
        <button onClick={load} className="rn3-btn-ghost text-sm" data-testid="je-apply-filters">
          {lang === "es" ? "Aplicar" : "Apply"}
        </button>
        <div className="flex-1"></div>
        <button onClick={exportCsv} className="rn3-btn-ghost text-sm" data-testid="je-export-csv">
          <Download className="w-4 h-4" /> CSV
        </button>
        <button onClick={() => setShowNew(true)} className="rn3-btn-primary text-sm" data-testid="je-new-btn">
          <Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo asiento" : "New entry"}
        </button>
      </div>

      {/* Table */}
      <div className="border border-[#E5E5E5]">
        {loading ? (
          <div className="p-8 text-center text-[#737373] text-sm">{lang === "es" ? "Cargando…" : "Loading…"}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="je-empty">
            <BookMarked className="w-8 h-8 text-[#C4A252] mx-auto mb-3" />
            <div className="text-sm text-[#0A0A0A] mb-1">{lang === "es" ? "Sin asientos manuales" : "No journal entries yet"}</div>
            <div className="text-xs text-[#737373]">{lang === "es" ? "Los asientos manuales aparecerán aquí" : "Manual accruals, adjustments, and closing entries appear here."}</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7]">
              <tr className="text-left border-b border-[#E5E5E5]">
                <th className="py-2.5 px-3 rn3-eyebrow">{lang === "es" ? "Fecha" : "Date"}</th>
                <th className="py-2.5 px-3 rn3-eyebrow">Memo</th>
                <th className="py-2.5 px-3 rn3-eyebrow text-right">DR / CR</th>
                <th className="py-2.5 px-3 rn3-eyebrow">{lang === "es" ? "Líneas" : "Lines"}</th>
                <th className="py-2.5 px-3 rn3-eyebrow">{lang === "es" ? "Estado" : "Status"}</th>
                <th className="py-2.5 px-3 rn3-eyebrow text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((je) => (
                <tr key={je.id} className="border-b border-[#F5F5F5] hover:bg-[#FDFBF7]/50" data-testid={`je-row-${je.id}`}>
                  <td className="py-2.5 px-3 whitespace-nowrap">{je.date}</td>
                  <td className="py-2.5 px-3">
                    <div>{je.memo}</div>
                    {je.reverses_je_id && <div className="text-[10px] text-[#8B1C22] italic mt-0.5">reverses {je.reverses_je_id.slice(0, 8)}</div>}
                  </td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap font-medium">{fmt(je.total_debit)}</td>
                  <td className="py-2.5 px-3">
                    <div className="space-y-0.5 text-xs">
                      {je.lines.map((l, i) => (
                        <div key={i} className="text-[#404040]">
                          {l.account_code} — {l.account_name} · {Number(l.debit) > 0 ? `DR ${fmt(l.debit)}` : `CR ${fmt(l.credit)}`}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${je.status === "posted" ? "bg-white border-[#15803D] text-[#15803D]" : "bg-[#FDFBF7] border-[#737373] text-[#737373]"}`}>
                      {je.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex gap-2 justify-end">
                      {je.status === "posted" && !je.reverses_je_id && (
                        <>
                          <button onClick={() => setEditing(je)} className="text-[#737373] hover:text-[#8B1C22]" title={lang === "es" ? "Editar" : "Edit"} data-testid={`je-edit-${je.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => reverse(je)} className="text-[#737373] hover:text-[#8B1C22]" title={lang === "es" ? "Revertir" : "Reverse"} data-testid={`je-reverse-${je.id}`}>
                            <RotateCw className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {je.status !== "reversed" && (
                        <button onClick={() => remove(je)} className="text-[#737373] hover:text-[#8B1C22]" title={lang === "es" ? "Eliminar" : "Delete"} data-testid={`je-delete-${je.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title={lang === "es" ? "Nuevo asiento" : "New Journal Entry"} testid="je-new-modal">
        <EntryForm
          book={book}
          accounts={accounts}
          initial={null}
          lang={lang}
          onCancel={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      </Modal>
      <Modal open={!!editing} onClose={() => setEditing(null)} title={lang === "es" ? "Editar asiento" : "Edit Journal Entry"} testid="je-edit-modal">
        {editing && (
          <EntryForm
            book={book}
            accounts={accounts}
            initial={editing}
            lang={lang}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </Modal>
    </div>
  );
};

export default JournalTab;
